import { FormEvent, ReactNode, useCallback, useEffect, useRef } from "react";
import { Send } from "lucide-react";
import { chatSendButtonClassName } from "@/components/chat/chat-controls";
import { resizeComposerTextarea } from "@/components/chat/resizeComposerTextarea";
import { cn } from "@/lib/utils";

const COMPOSER_MAX_HEIGHT_PX = 160;
/** ~3 lines: text-sm leading-5 + py-1.5 — matches Copilot-style composer footprint */
const COMPOSER_MIN_HEIGHT_PX = 72;
/** Single line for narrow inline assistant sidebars */
const COMPOSER_MIN_HEIGHT_COMPACT_PX = 40;

export interface MessageComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  disabled?: boolean;
  canSubmit?: boolean;
  placeholder?: string;
  error?: string | null;
  formClassName?: string;
  children?: ReactNode;
  sendTitle?: string;
  sendAriaLabel?: string;
  /** Single-line minimum height; use on inline write assistants */
  compact?: boolean;
}

export function MessageComposer({
  value,
  onChange,
  onSubmit,
  disabled = false,
  canSubmit = false,
  placeholder = "Message copilot…",
  error = null,
  formClassName,
  children,
  sendTitle = "Send message",
  sendAriaLabel = "Send message",
  compact = false,
}: MessageComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const minHeightPx = compact ? COMPOSER_MIN_HEIGHT_COMPACT_PX : COMPOSER_MIN_HEIGHT_PX;

  const syncTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      resizeComposerTextarea(textarea, minHeightPx, COMPOSER_MAX_HEIGHT_PX);
    }
  }, [minHeightPx]);

  useEffect(() => {
    syncTextareaHeight();
    const id = requestAnimationFrame(() => syncTextareaHeight());
    return () => cancelAnimationFrame(id);
  }, [value, placeholder, syncTextareaHeight, compact]);

  return (
    <form
      onSubmit={onSubmit}
      className={cn(
        "shrink-0 border-t border-[rgba(15,23,42,0.09)] bg-[var(--composer-bar-bg)] backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/80",
        compact ? "p-3" : "p-4",
        formClassName
      )}
    >
      {error ? (
        <p className="mb-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-300">
          {error}
        </p>
      ) : null}
      {children}
      <div
        className={cn(
          "flex items-end gap-1 rounded-xl border border-[rgba(15,23,42,0.09)] bg-[var(--composer-bg)] p-1.5 shadow-[var(--shadow-card)]",
          "focus-within:border-blue-500/50 focus-within:ring-2 focus-within:ring-blue-500/20",
          "dark:border-white/10 dark:bg-white/5 dark:shadow-none"
        )}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            resizeComposerTextarea(event.target, minHeightPx, COMPOSER_MAX_HEIGHT_PX);
          }}
          placeholder={placeholder}
          rows={compact ? 1 : 3}
          disabled={disabled}
          style={{ minHeight: minHeightPx }}
          className={cn(
            "max-h-40 min-w-0 flex-1 resize-none border-0 bg-transparent py-1.5 pl-2 pr-1 text-sm leading-5",
            compact ? "min-h-[40px]" : "min-h-[72px]",
            "text-[#101828] placeholder-[#576274] focus:outline-none focus:ring-0",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "dark:text-white dark:placeholder-gray-500"
          )}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              onSubmit(event);
            }
          }}
        />
        <button
          type="submit"
          disabled={disabled || !canSubmit}
          className={cn(
            chatSendButtonClassName(canSubmit && !disabled),
            "flex size-9 shrink-0 items-center justify-center rounded-lg !p-0"
          )}
          title={sendTitle}
          aria-label={sendAriaLabel}
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}
